<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="flex items-stretch justify-between mt-2.5">
        <div class="element-island">
          <h1 class="text-3xl font-bold text-gray-900">
            {{ $t('HeadContract.VariationsSchedule.title') }}
          </h1>

          <p class="mt-2 text-sm text-gray-600">
            {{ $t('HeadContract.VariationsSchedule.subTitle') }}
          </p>
        </div>

        <div
          v-if="project?.unarchived && !isReadOnly"
          class="element-island flex items-center gap-3 flex-wrap justify-end"
        >
          <button
            type="button"
            class="btn btn--secondary-blue space-x-2 flex items-center"
            @click="showSummaryReportModal = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>

            <span>{{ $t('HeadContract.VariationsSchedule.summaryReportButton') }}</span>
          </button>

          <button
            type="button"
            class="btn btn--secondary-blue space-x-2 flex items-center"
            @click="showManageCategoriesModal = true"
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
    />
</svg>

            <span>Categories</span>
          </button>

          <button
            type="button"
            class="btn btn--secondary-blue space-x-2 flex items-center"
            @click="showCreateVariationModal = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <span>{{ $t('HeadContract.VariationsSchedule.button') }}</span>
          </button>
        </div>
      </div>

      <div class="element-island overflow-y-auto flex flex-col flex-1 p-4">
        <template v-if="variationOrders.length">
          <div
            v-show="!variationsExpanded"
            class="flex space-x-4 mb-4"
          >
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 w-1/3">
              <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-3 rounded-t-xl">
                <div class="flex items-center justify-between">
                  <h2 class="font-semibold text-white">
                    {{ $t('HeadContract.VariationsSchedule.projectSummary') }}
                  </h2>

                  <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="space-y-4">
                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.contractValue') }}</span>

                    <span class="text-sm font-semibold text-gray-900">{{ $tcc(totalTradesBudget) }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.VariationsSchedule.approvedVariations') }}
                      <span class="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {{ $tn(variationsSummary.approved_count + variationsSummary.completed) }}
                      </span>
                    </span>

                    <span class="text-sm font-semibold text-green-600">{{ $tcc(variationsSummary.approved_variations_total + variationsSummary.completed_variations_total) }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.currentAdjustedValue') }}</span>

                    <span class="text-sm font-semibold text-blue-600">{{ $tcc(totalTradesBudget + variationsSummary.approved_variations_total + variationsSummary.completed_variations_total) }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.VariationsSchedule.unapprovedVariations') }}
                      <span class="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {{ $tn(variationsSummary.unapproved_count) }}
                      </span>
                    </span>

                    <span class="text-sm font-semibold text-yellow-600">{{ $tcc(variationsSummary.unapproved_variations_total) }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.finalEstimatedValue') }}</span>

                    <span class="text-sm font-semibold text-gray-900">{{ $tcc(totalTradesBudget + totalVariationsBudget) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-200 w-1/3">
              <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-3 rounded-t-xl">
                <div class="flex items-center justify-between">
                  <h2 class="font-semibold text-white">
                    {{ $t('HeadContract.VariationsSchedule.costSynopsis') }}
                  </h2>

                  <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-7 h-7 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
</svg>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="space-y-4">
                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.forecasted') }}</span>

                    <span class="text-sm font-semibold text-blue-600">{{ $tcc(variationsSummary.priced) }}</span>
                  </div>

                  <div
                    v-if="project?.isContractor"
                    class="flex justify-between items-center py-2 border-b border-gray-100"
                  >
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.submitted') }}</span>

                    <span class="text-sm font-semibold text-purple-600">{{ $tcc(variationsSummary.submitted) }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.assessed') }}</span>

                    <span class="text-sm font-semibold text-orange-600">{{ $tcc(variationsSummary.provisioned) }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.principled') }}</span>

                    <span class="text-sm font-semibold text-yellow-600">{{ $tcc(variationsSummary.principled) }}</span>
                  </div>

                  <div class="flex justify-between items-center py-2">
                    <span class="text-sm font-medium text-gray-600">{{ $t('HeadContract.VariationsSchedule.approved') }}</span>

                    <span class="text-sm font-semibold text-green-600">{{ $tcc(variationsSummary.approved) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="flex flex-col space-y-4 w-1/3"
            >
              <div class="flex h-full">
                <div class="w-1/2 my-auto">
                  <p class="text-center text-lg font-semibold">
                    {{ $t('HeadContract.VariationsSchedule.variationsByStatus') }}
                  </p>

                  <VariationsStatusGraph
                    class="mt-6"
                    :variation-orders-by-status="variationOrdersByStatus"
                    :variations-summary="variationsSummary"
                  />
                </div>

                <div class="w-1/2 my-auto">
                  <p class="text-center text-lg font-semibold">
                    {{ $t('HeadContract.VariationsSchedule.variationStatusByValue') }}
                  </p>

                  <VariationsValueGraph
                    class="mt-6"
                    :variation-orders-by-status="variationOrdersByStatus"
                    :variations-summary="variationsSummary"
                    :total-variations-budget="totalVariationsBudget"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="fetchVariationsAction.firstLoad.value"
            ref="variationsContainer"
            class="w-full overflow-y-auto relative"
          >
            <div class="flex flex-col w-full border border-gray-200 rounded-lg shadow-sm">
              <div
                class="px-2 py-4 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0"
                :class="{ 'z-30': increaseZIndex }"
              >
                <div class="space-y-4">
                  <!-- Filter Header -->
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <button
                        type="button"
                        class="group p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                        @click="toggleExpand"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="variationsExpanded"
                         
                          class="w-6 h-6 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                         
                          class="w-6 h-6 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                      </button>

                      <h3 class="text-lg font-semibold text-gray-900">
                        {{ $t('HeadContract.VariationsSchedule.variations') }}
                      </h3>

                      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                        <!-- Search Filter -->
                        <div class="space-y-2">
                          <div class="relative">
                            <AppInputIcon
                              v-model="searchForm.form.search"
                              type="text"
                              name="variation_orders_filter"
                              class="form-input w-full"
                              :placeholder="$t('HeadContract.VariationsSchedule.placeholder.searchVariations')"
                            >
                              <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
                              </template>
                            </AppInputIcon>
                          </div>
                        </div>

                        <!-- Status Filter -->
                        <div class="space-y-2">
                          <AppSelect
                            v-model="searchForm.form.status"
                            name="status_filter"
                            class="w-full capitalize form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :options="statusOptions"
                            :empty-text="$t('HeadContract.VariationsSchedule.placeholder.allStatuses')"
                          />
                        </div>

                        <!-- Assignee Filter -->
                        <div
                          v-if="!isReadOnly"
                          class="space-y-2"
                        >
                          <AppTypeAhead
                            v-model="nextActionUserName"
                            name="next_action_assignee_id"
                            class="w-full form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :placeholder="$t('HeadContract.VariationsSchedule.placeholder.searchUsers')"
                            :suggestion-callback="getSuggestions"
                            :initial-suggestions="getSuggestions"
                            @value-selected="selectUser"
                          >
                            <template #icon>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>
                            </template>
                          </AppTypeAhead>
                        </div>

                        <!-- Sort Filter -->
                        <div class="space-y-2">
                          <AppSelect
                            v-model="sortColumn"
                            name="sort_filter"
                            class="w-full form-input pr-8 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            :options="sortOptions"
                            :empty-text="$t('HeadContract.VariationsSchedule.placeholder.selectSortOption')"
                          />
                        </div>

                        <!-- Actions -->
                        <div class="space-y-2">
                          <button
                            type="button"
                            class="btn btn--secondary-blue w-full text-sm"
                            @click="resetSearch"
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>

                    <Transition
                      mode="out-in"
                      enter-active-class="animate__animated animate__fadeIn animate__faster"
                      leave-active-class="animate__animated animate__fadeOut animate__faster"
                    >
                      <p
                        v-if="filteredVariations.length > 0"
                        key="total-variations"
                        class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                      >
                        <span class="font-medium text-gray-900">{{ filteredVariations.length }}&nbsp;</span>

                        <span class="text-gray-500"> {{ $t('HeadContract.VariationsSchedule.variation', filteredVariations.length) }}</span>
                      </p>
                    </Transition>
                  </div>

                  <!-- Active Filters Display -->
                  <div
                    v-if="hasActiveFilters"
                    class="flex items-center space-x-2 pt-2"
                  >
                    <span class="text-sm text-gray-500">{{ $t('HeadContract.VariationsSchedule.activeFilters') }}:</span>

                    <div class="flex flex-wrap gap-2">
                      <span
                        v-if="searchForm.form.search"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {{ $t('HeadContract.VariationsSchedule.search') }}: "{{ searchForm.form.search }}"
                        <button
                          type="button"
                          class="ml-1.5 text-blue-600 hover:text-blue-800"
                          @click="searchForm.form.search = ''"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>

                      <span
                        v-if="!falsey(searchForm.form.status)"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :style="{ color: VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, searchForm.form.status)], backgroundColor: VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, searchForm.form.status)] }"
                      >
                        {{ $t(`Models.VariationOrder.statusTitles.${enumKeyFromValue(VariationOrderStatus, searchForm.form.status)}`) }}
                        <button
                          type="button"
                          class="ml-1.5"
                          @click="searchForm.form.status = undefined"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>

                      <span
                        v-if="nextActionUserName"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-300 text-gray-700"
                      >
                        {{ nextActionUserName }}
                        <button
                          type="button"
                          class="ml-1.5 text-gray-600 hover:text-gray-800"
                          @click="clearUserFilter"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-3 h-3">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
</svg>
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <table
                class="border-separate border-spacing-0 w-full z-10 text-sm"
              >
                <thead>
                  <tr class="child:px-2 child:py-3 z-10 sticky top-0 child:border-y child:border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                    <th class="text-left w-60">
                      <div class="flex items-center space-x-2">
                        <button
                          v-if="y > 60"
                          type="button"
                          @click="toggleExpand"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="variationsExpanded"
                           
                            class="w-5 h-5 hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
                           
                            class="w-5 h-5 hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>
                        </button>

                        <span>
                          {{ $t('HeadContract.VariationsSchedule.variation') }}
                        </span>
                      </div>
                    </th>

                    <th class="text-left">
                      {{ $t('HeadContract.VariationsSchedule.dateOfOccurrence') }}
                    </th>

                    <th class="text-left">
                      {{ project?.isContractor ? $t('HeadContract.VariationsSchedule.clientRef') : $t('HeadContract.VariationsSchedule.contractorRef') }}
                    </th>

                    <th class="text-left">
                      {{ $t('HeadContract.VariationsSchedule.status') }}
                    </th>

                    <th class="text-right ">
                      {{ $t('HeadContract.VariationsSchedule.amount') }}
                    </th>

                    <th
                      v-if="project?.unarchived"
                      class="text-left w-60"
                    >
                      {{ $t('HeadContract.VariationsSchedule.nextAction') }}
                    </th>

                    <th
                      v-if="project?.unarchived && !isReadOnly"
                      class="text-left"
                    >
                      {{ $t('HeadContract.VariationsSchedule.assignee') }}
                    </th>

                    <th
                      v-if="project?.unarchived && !isReadOnly"
                      class="text-left min-w-28"
                    />
                  </tr>
                </thead>

                <tbody>
                  <tr v-if="fetchVariationsAction.is(States.LOADING)">
                    <td
                      :colspan="variationTableColspan"
                      class="px-6 py-16 text-center"
                    >
                      <div class="flex flex-col items-center">
                        <AppSpinner class="h-8 w-8 mb-4" />

                        <h3 class="text-sm font-medium text-gray-900 mb-1">
                          {{ $t('HeadContract.VariationsSchedule.loadingVariations') }}
                        </h3>
                      </div>
                    </td>
                  </tr>

                  <template v-else-if="fetchVariationsAction.is(States.COMPLETE) && filteredVariations.length > 0">
                    <template
                      v-for="row in groupedScheduleRows"
                      :key="row.type === 'category' ? `category-${row.id}` : row.variation.id"
                    >
                      <VariationCategoryHeaderRow
                        v-if="row.type === 'category'"
                        :name="row.name"
                        :colspan="variationTableColspan"
                      />

                      <VariationOrderRow
                        v-else
                        :variation-order="row.variation"
                        :read-only="isReadOnly"
                        @show-variation="showVariation(row.variation)"
                        @show-variation-attachments="showVariationAttachments(row.variation)"
                        @open-comment-modal="openCommentModal(row.variation)"
                        @accept-variation-order="acceptVariationOrder(row.variation)"
                        @decline-variation-order="declineVariationOrder(row.variation)"
                        @price-variation-order="selectedVariation = row.variation.id; showModal = true;"
                        @submit-variation-order="submitVariationOrder(row.variation.id)"
                        @approve-variation-order="approveVariationOrder(row.variation.id)"
                        @sum-set-variation-order="priceVariationOrder($event.variationOrder, $event.sumPrice)"
                        @save-next-action="updateNextActionVariationOrder($event.variationOrder, $event.next_action, $event.next_action_due_date, $event.next_action_assignee_id)"
                        @send-notice="sendNoticeForVariationOrder($event)"
                        @register-notice="registerNotice($event)"
                        @register-pricing-notice="registerPricingNotice($event)"
                        @open-correspondence-modal="openCorrespondenceModal($event)"
                        @principle-variation-order="principleVariationOrder($event)"
                      />
                    </template>

                    <tr
                      v-if="!searchForm.form.search && fetchVariationsAction.is(States.COMPLETE)"
                      class="sticky bottom-0 bg-white border child:px-2 child:border-y first-child:border-l last-child:border-r font-semibold"
                    >
                      <td />

                      <td />

                      <td />

                      <td>
                        {{ $t('HeadContract.VariationsSchedule.total') }}
                      </td>

                      <td class="text-right">
                        {{ $tcc(totalVariationsBudget) }}
                      </td>

                      <td v-if="project?.unarchived" />

                      <td v-if="project?.unarchived && !isReadOnly" />

                      <td v-if="project?.unarchived && !isReadOnly" />
                    </tr>
                  </template>

                  <tr v-else-if="fetchVariationsAction.is(States.COMPLETE)">
                    <td
                      :colspan="variationTableColspan"
                      class="px-6 py-16 text-center"
                    >
                      <div class="flex flex-col items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                        <h3 class="text-sm font-medium text-gray-900 mb-1">
                          {{ $t('HeadContract.VariationsSchedule.noVariationsFound') }}
                        </h3>

                        <p
                          class="text-sm text-gray-500"
                        >
                          {{ $t('HeadContract.VariationsSchedule.adjustFilters') }}
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <AppSpinner
            v-else
            class="mx-auto mt-5"
          />
        </template>

        <div
          v-else-if="fetchVariationsAction.is(States.LOADING)"
          class="flex w-full"
        >
          <AppSpinner class="mx-auto" />
        </div>

        <div
          v-else
          class="flex flex-col items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="mx-auto h-12 w-12 text-gray-300 mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.VariationsSchedule.noVariationsFound') }}
          </h3>

          <p
            class="text-sm text-gray-500"
          >
            {{ $t('HeadContract.VariationsSchedule.createFirstVariation') }}
          </p>
        </div>
      </div>
    </div>

    <PricingModal
      v-if="showModal"
      :variation-order-id="selectedVariation"
      @close="showModal = false"
      @priced="fetchSummary"
    />

    <VariationSummaryReportModal
      v-if="showSummaryReportModal"
      :project-id="props.projectId"
      @close="showSummaryReportModal = false"
    />

    <VariationSaveModal
      v-if="showCreateVariationModal"
      :project-id="$props.projectId"
      @close="showCreateVariationModal = false"
    />

    <ProjectVariationOrderCategoriesModal
      v-if="showManageCategoriesModal"
      :project-id="$props.projectId"
      @close="showManageCategoriesModal = false"
    />

    <VariationShowModal
      v-if="showVariationModal"
      :variation-order-id="selectedVariation"
      :start-on-attachments="startOnAttachments"
      @transfer-variation="showVariationModal = false; showTransferModal = true"
      @close="closeShowModal"
    />

    <VariationCommentModal
      v-if="showCommentModal"
      :project-id="props.projectId"
      :variation-id="selectedVariation"
      @close="showCommentModal = false"
    />

    <RegisterNoticeModal
      v-if="showRegisterModal"
      :variation-order-id="selectedVariation"
      @close="showRegisterModal = false"
    />

    <RegisterPricingModal
      v-if="showPricingRegisterModal"
      :variation-order-id="selectedVariation"
      @close="showPricingRegisterModal = false"
      @priced="fetchSummary"
    />

    <VariationCorrespondenceModal
      v-if="showVariationCorrespondenceModal"
      :variation-order-id="selectedVariation"
      @close="showVariationCorrespondenceModal = false"
    />

    <VariationTransferModal
      v-if="showTransferModal"
      :variation-order-id="selectedVariation"
      :project-id="project.id"
      @close="showTransferModal = false"
      @transferred="fetchVariationsAction.request(project.id)"
    />
  </div>
</template>

<script setup lang="ts">
import PricingModal from '@/components/HeadContract/VariationsSchedule/PricingModal.vue';
import ProjectVariationOrderCategoriesModal from '@/components/HeadContract/VariationsSchedule/ProjectVariationOrderCategoriesModal.vue';
import RegisterNoticeModal from '@/components/HeadContract/VariationsSchedule/RegisterNoticeModal.vue';
import RegisterPricingModal from '@/components/HeadContract/VariationsSchedule/RegisterPricingModal.vue';
import VariationCategoryHeaderRow from '@/components/HeadContract/VariationsSchedule/VariationCategoryHeaderRow.vue';
import VariationCommentModal from '@/components/HeadContract/VariationsSchedule/VariationCommentModal.vue';
import VariationCorrespondenceModal from '@/components/HeadContract/VariationsSchedule/VariationCorrespondenceModal.vue';
import VariationOrderRow from '@/components/HeadContract/VariationsSchedule/VariationOrderRow.vue';
import VariationSaveModal from '@/components/HeadContract/VariationsSchedule/VariationSaveModal.vue';
import VariationShowModal from '@/components/HeadContract/VariationsSchedule/VariationShowModal.vue';
import VariationsStatusGraph from '@/components/HeadContract/VariationsSchedule/VariationsStatusGraph.vue';
import VariationSummaryReportModal from '@/components/HeadContract/VariationsSchedule/VariationSummaryReportModal.vue';
import VariationsValueGraph from '@/components/HeadContract/VariationsSchedule/VariationsValueGraph.vue';
import VariationTransferModal from '@/components/HeadContract/VariationsSchedule/VariationTransferModal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, cumulativeSum, enumKeyFromValue, falsey, roundDecimals, timeZone } from '@/helpers';
import { t } from '@/i18n';
import VariationOrder, {
  VariationOrderStatus,
  VariationOrderStatusColours,
  VariationOrderStatusTextColours,
  VariationOrderStatusTitles,
} from '@/models/VariationOrder';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { useDebounceFn, useScroll } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { useCollect } from 'pinia-orm/dist/helpers';
import Swal from 'sweetalert2';
import { useTemplateRef } from 'vue';
import { nextTick } from 'vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  projectId: string;
  variationId?: string;
}>();

// pass through isClientManager to components -> hide actions, hide show modal, hide correspondences, hide assignee, hide top right buttons
const userStore = useUserStore();
const isReadOnly = userStore.isClientManager();
const variationOrdersStore = useVariationOrdersStore();
const variationOrderCategoriesStore = useVariationOrderCategoriesStore();
const projectsStore = useProjectsStore();
const tradesStore = useTradesStore();

const project = computed(() => {
  return projectsStore.project;
});

const variationsSummary = ref<VariationOrdersSummary>({
  approved_variations_total: 0,
  unapproved_variations_total: 0,
  principled: 0,
  completed_variations_total: 0,
  priced: 0,
  submitted: 0,
  completed: 0,
  provisioned: 0,
  approved: 0,
  total_variations: 0,
  approved_count: 0,
  unapproved_count: 0,
  total_budget: 0,
});

const selectedVariation = ref();
const showVariationModal = ref(false);
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);
const fetchVariationsAction = useStoreApiAction(variationOrdersStore.fetchVariationOrders);
const fetchVariationOrderCategoriesAction = useStoreApiAction(variationOrderCategoriesStore.indexProject);
const fetchVariationsSummaryAction = useStoreApiAction(variationOrdersStore.fetchVariationOrdersSummary);
const fetchSubcontractVariationOrdersAction = useStoreApiAction(variationOrdersStore.fetchSubcontractVariationOrders);

const fetchSummary = () => {
  fetchVariationsSummaryAction.request(project.value.id).then((summary) => {
    variationsSummary.value = summary;
  }).catch(console.error);
};

const showTransferModal = ref(false);
const startOnAttachments = ref(false);

watch(() => project.value?.id, (newValue) => {
  if(newValue) {
    fetchVariationsAction.request(newValue);

    fetchVariationOrderCategoriesAction.request(newValue).catch(console.error);
    fetchSubcontractVariationOrdersAction.request(newValue).catch(console.error);

    fetchTradesAction.request(newValue, { boq_type: 'direct' });

    fetchVariationsSummaryAction.request(newValue).then((summary) => {
      variationsSummary.value = summary;
    }).catch(console.error);

    if(props.variationId && !isReadOnly) {
      selectedVariation.value = parseInt(props.variationId);
      startOnAttachments.value = false;
      showVariationModal.value = true;
    }
  }
}, { immediate: true });

const router = useRouter();

const showVariation = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  startOnAttachments.value = false;

  showVariationModal.value = true;

  router.replace({ params: { variationId: variation.id.toString() } });
};

const showVariationAttachments = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;
  startOnAttachments.value = true;

  showVariationModal.value = true;

  router.replace({ params: { variationId: variation.id.toString() } });
};

const closeShowModal = () => {
  showVariationModal.value = false;
  startOnAttachments.value = false;

  router.replace({ params: { variationId: '' } });
};

watch(project, (newValue) => {
  if(newValue && !newValue.boq_locked) {
    router.push({ name: 'BillOfQuantities', params: { projectId: newValue.id } });
  }
}, { immediate: true });

const sortColumn = ref<'status' | 'category' | 'code' | 'date_of_occurrence' | 'created_at' | 'amount' | 'next_action'>(
  'category',
);

const sortOptions = computed(() => {
  const options = [
    {
      value: 'category',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.category'),
    },
    {
      value: 'status',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.status'),
    },
    // {
    //   value: 'code',
    //   label: 'Code',
    // },
    {
      value: 'date_of_occurrence',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.dateOfOccurrence'),
    },
    {
      value: 'created_at',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.latest'),
    },
    {
      value: 'amount',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.amount'),
    },
    {
      value: 'next_action',
      label: t('HeadContract.VariationsSchedule.inputs.sortBy.nextActionDueDate'),
    },
  ];

  return options;
});

const variationOrders = computed(() => {
  return variationOrdersStore.models
    .whereNull('package_id')
    .where('project_id', parseInt(props.projectId))
    .with('trade_items')
    .with('next_action_assignee')
    .with('variation_order_category')
    .with('transferred_to_variation_order', (query) => query.with('project'))
    .orderBy('status')
    .get();
});

const sortedVariationOrders = computed(() => {
  let variations = [];

  switch (sortColumn.value) {
    case 'category':

      // fall-through
    case 'status':
      variations = useCollect(variationOrders.value).sortBy([['status', 'asc'], ['created_at', 'desc']]);
      break;

    case 'date_of_occurrence':

      // fall-through
    case 'created_at':
      variations = useCollect(variationOrders.value).sortBy([[sortColumn.value, 'desc']]);
      break;

    case 'amount':
      variations = useCollect(variationOrders.value).sortBy((model) => -model.totalBudget);

      break;

    case 'code':
      variations = variationOrdersStore.models
        .with('trade_items')
        .with('next_action_assignee')
        .with('transferred_to_variation_order', (query) => query.with('project'))
        .where('project_id', parseInt(props.projectId))
        .whereNull('package_id')
        .get()
        .sort((a, b) => {
          if(!falsey(a.code) && !falsey(b.code)) {
            return a.code.localeCompare(b.code);
          } else if(falsey(a.code) && !falsey(b.code)) {
            return 1;
          }

          return -1;
        });

      break;

    case 'next_action':
      variations = variationOrdersStore.models
        .with('next_action_assignee')
        .with('trade_items')
        .with('transferred_to_variation_order', (query) => query.with('project'))
        .where('project_id', parseInt(props.projectId))
        .whereNull('package_id')
        .get()
        .sort((a, b) => {
          if(!falsey(a.nextActionDueIn) && !falsey(b.nextActionDueIn)) {
            if(a.nextActionDueIn < 0) {
              return 1;
            }

            if(b.nextActionDueIn < 0) {
              return -1;
            }

            return a.nextActionDueIn - b.nextActionDueIn;
          } else if(falsey(a.nextActionDueIn) && !falsey(b.nextActionDueIn)) {
            return 1;
          }

          return -1;
        });

      break;
  }

  return variations;
});

const totalVariationsBudget = computed(() => {
  return variationsSummary.value.unapproved_variations_total + variationsSummary.value.approved_variations_total +
    variationsSummary.value.completed_variations_total;
});

const variationOrdersByStatus = computed(() => {
  return variationOrders.value.reduce((variationsMap, variation) => {
    const key = enumKeyFromValue(VariationOrderStatus, variation.status);

    if(key === 'declined') {
      return variationsMap;
    }

    if(variationsMap.has(key)) {
      const variations = variationsMap.get(key);

      variations.push(variation);
      variationsMap.set(key, variations);
    } else {
      variationsMap.set(key, [variation]);
    }

    return variationsMap;
  }, new Map<string, VariationOrder[]>());
});

const acceptVariationOrderAction = useStoreApiAction(variationOrdersStore.acceptVariationOrder);
const declineVariationOrderAction = useStoreApiAction(variationOrdersStore.declineVariationOrder);
const submitVariationOrderAction = useStoreApiAction(variationOrdersStore.submitVariationOrder);
const approveVariationOrderAction = useStoreApiAction(variationOrdersStore.approveVariationOrder);
const principleVariationOrderAction = useStoreApiAction(variationOrdersStore.principleVariationOrder);
const priceVariationOrderAction = useStoreApiAction(variationOrdersStore.priceVariationOrder);
const sendNoticeForVariationOrderAction = useStoreApiAction(variationOrdersStore.sendNoticeForVariationOrder);
const updateNextActionVariationOrderAction = useStoreApiAction(variationOrdersStore.updateNextActionVariationOrder);

const updateNextActionVariationOrder = (
  variationOrder: VariationOrder,
  next_action: string,
  next_action_due_date: string,
  next_action_assignee_id: number,
) => {
  updateNextActionVariationOrderAction.request(variationOrder.id, {
    next_action,
    next_action_due_date,
    next_action_assignee_id,
  }).catch(console.error);
};

const acceptVariationOrder = (variationOrder: VariationOrder) => {
  Swal.fire({
    title: 'Accept Variation Order?',
    icon: 'question',
    text: variationOrder.description,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      acceptVariationOrderAction.request(variationOrder.id).then(() => {
        console.log('accepted');
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const declineVariationOrder = (variationOrder: VariationOrder) => {
  Swal.fire({
    title: 'Decline Variation Order?',
    icon: 'question',
    text: variationOrder.description,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      declineVariationOrderAction.request(variationOrder.id).then(() => {
        console.log('declined');
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const submitVariationOrder = (id: Id) => {
  Swal.fire({
    title: 'Submit Variation Order?',
    text: 'Submit variation order pricing to be approved by client',
    icon: 'question',
    showCancelButton: true,
    inputLabel: `Additional information ${
      project.value.head_contract_settings.variation_submission_additional_info_required ? '(required)' : ''
    }:`,

    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      submitVariationOrderAction.request(id, { description: result.value, time_zone: timeZone }).then((data) => {
        toast.success('Variation Order Submitted');
      }).catch((error) => {
        Swal.fire('Failed to Submit', error?.data?.message ?? 'Failed to submit variation order', 'error');
      });
    }
  });
};

const approveVariationOrder = (id: Id) => {
  Swal.fire({
    title: 'Approve Variation Order?',
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      approveVariationOrderAction.request(id, { time_zone: timeZone }).then((data) => {
        console.log(data);
      }).catch((error) => {
        Swal.fire('Failed to Approve', '', 'error');
      });
    }
  });
};

const principleVariationOrder = (variation: VariationOrder) => {
  Swal.fire({
    title: 'Approve in Principle?',
    text: `Would you like to set variation ${variation.code} to approved in principle?`,
    icon: 'question',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      principleVariationOrderAction.request(variation.id).then((data) => {
        console.log(data);
      }).catch((error) => {
        Swal.fire('Failed to Approve in Principle', '', 'error');
      });
    }
  });
};

const priceVariationOrder = (variationOrder: VariationOrder, budget: number) => {
  priceVariationOrderAction.request(variationOrder.id, { budget }).then(() => {
    fetchVariationsSummaryAction.request(project.value.id).then((summary) => {
      variationsSummary.value = summary;
    });
  }).catch((error) => {
    console.log(error);
  });
};

const sendNoticeForVariationOrder = (variationOrder: VariationOrder) => {
  Swal.fire({
    title: 'Send Notice for Variation Order?',
    icon: 'question',
    showCancelButton: true,
    inputLabel: `Additional information ${
      project.value.head_contract_settings.nov_additional_info_required ? '(required)' : ''
    }:`,

    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      sendNoticeForVariationOrderAction.request(variationOrder.id, { description: result.value, time_zone: timeZone })
        .then((data) => {
          toast.success('Notice Sent');
        }).catch((error) => {
          Swal.fire('Failed to Send Notice', error?.data?.message, 'error');
        });
    }
  });
};

const showModal = ref(false);
const showSummaryReportModal = ref(false);
const showCreateVariationModal = ref(false);
const showManageCategoriesModal = ref(false);

const trades = computed(() => {
  return tradesStore.models.where('project_id', project.value?.id).where('boq_type', 'direct').get();
});

const totalTradesBudget = computed(() => {
  return trades.value.reduce((total, current) => {
    return total + current.totalAdjustedBudget;
  }, 0);
});

const searchForm = useFormData({
  search: '',
  status: undefined,
  next_action_assignee_id: undefined,
});

const nextActionUserName = ref('');

const statusOptions = computed(() => {
  const options = [];

  for(const key in VariationOrderStatus) {
    options.push({ value: VariationOrderStatus[key], label: VariationOrderStatusTitles[key] });
  }

  return options;
});

const filteredVariationIds = ref<number[]>();

const searchVariations = () => {
  fetchVariationsAction.request(project.value?.id, searchForm.form).then(({ data: variations }) => {
    filteredVariationIds.value = variations.map((variation) => variation.id);
  }).catch((error) => {
    console.log(error);
  });
};

const filteredVariations = computed(() => {
  if(!searchForm.isDirty.value) {
    return sortedVariationOrders.value;
  }

  return sortedVariationOrders.value.filter((variationOrder) => {
    return filteredVariationIds.value?.includes(variationOrder.id);
  });
});

const variationOrderCategories = computed(() => {
  return variationOrderCategoriesStore.models
    .where('project_id', parseInt(props.projectId))
    .whereNull('package_id')
    .get();
});

const variationTableColspan = computed(() => {
  if(!project.value?.unarchived) {
    return 5;
  }

  if(isReadOnly) {
    return 6;
  }

  return 8;
});

const groupedScheduleRows = computed(() => {
  if(sortColumn.value !== 'category') {
    return filteredVariations.value.map((variation) => ({
      type: 'variation' as const,
      variation,
    }));
  }

  const uncategorized: VariationOrder[] = [];
  const grouped = new Map<number, { name: string; sortOrder: number; variations: VariationOrder[] }>();

  const categoriesById = new Map(
    variationOrderCategories.value.map((category) => [category.id, category]),
  );

  for(const variation of filteredVariations.value) {
    const categoryId = variation.variation_order_category_id;

    if(falsey(categoryId)) {
      uncategorized.push(variation);
      continue;
    }

    const existing = grouped.get(categoryId);

    if(existing) {
      existing.variations.push(variation);
      continue;
    }

    const category = categoriesById.get(categoryId) ?? variation.variation_order_category;

    grouped.set(categoryId, {
      name: category?.description ?? '',
      sortOrder: category?.sort_order ?? 0,
      variations: [variation],
    });
  }

  const rows: ({ type: 'category'; id: number; name: string } | { type: 'variation'; variation: VariationOrder })[] =
    uncategorized.map((variation) => ({
      type: 'variation',
      variation,
    }));

  [...grouped.entries()]
    .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
    .forEach(([id, group]) => {
      rows.push({
        type: 'category',
        id,
        name: group.name,
      });

      group.variations.forEach((variation) => {
        rows.push({
          type: 'variation',
          variation,
        });
      });
    });

  return rows;
});

const debounceSearch = useDebounceFn(searchVariations, 300);
const filterActive = ref(false);

watch(searchForm.form, (newValue, oldValue) => {
  if(
    !newValue.search && newValue.status === undefined && newValue.next_action_assignee_id === undefined &&
    filterActive.value
  ) {
    filterActive.value = false;
    searchVariations();
  } else {
    filterActive.value = true;
  }
});

watch(searchForm.form, () => {
  if(filterActive.value) {
    debounceSearch();
  }
});

const resetSearch = () => {
  filterActive.value = false;

  searchForm.resetData();

  searchVariations();
};

const showCommentModal = ref(false);

const openCommentModal = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;

  showCommentModal.value = true;
};

const showRegisterModal = ref(false);

const registerNotice = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;

  showRegisterModal.value = true;
};

const showPricingRegisterModal = ref(false);

const registerPricingNotice = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;

  showPricingRegisterModal.value = true;
};

const showVariationCorrespondenceModal = ref(false);

const openCorrespondenceModal = (variation: VariationOrder) => {
  selectedVariation.value = variation.id;

  showVariationCorrespondenceModal.value = true;
};

// const totalORders = ref(0);
// const ordersIndex = ref(0);
// const { wrapperProps } = useVirtualList(totalORders, ordersIndex, 40);
// const orderListRef = ref();
// const page = ref(1);
// const paginator = ref<ResourcePaginator<VariationOrder>>();

// const recordQueue = useQueue<number>(10);

// const initialFetch = () => {
//   variationOrdersStore.models.query().where('plant_id', props.plant.id).delete();

//   fetchVariationsAction.request(props.plant.id, plantsStore.filter.form).then((newPaginator) => {
//     recordQueue.reset();
//     totalRecords.value = 0;
//     totalRecords.value += newPaginator.data.length;

//     recordIndex.value = newPaginator.meta.current_page * newPaginator.meta.per_page;

//     recordQueue.enqueueFront(newPaginator.data.map((record: PlantDailyRecord) => {
//       return record.id;
//     }));

//     paginator.value = newPaginator;
//   });
// };

// const scrolled = () => {
//   const recordList = recordListRef.value;

//   if(recordList) {
//     const scrollable = recordList.scrollHeight > recordList.clientHeight;

//     if(
//       page.value > 1 &&
//       scrollable &&
//       recordList.scrollTop === 0
//     ) {
//       fetchRecordsAction.request(props.plant.id, { page: page.value - 1, ...plantsStore.filter.form })
//         .then((newPaginator) => {
//           paginator.value = newPaginator;

//           totalRecords.value -= newPaginator.data.length;
//           recordIndex.value = newPaginator.meta.current_page * newPaginator.meta.per_page;

//           const recordsToDelete = recordQueue.enqueueBack(newPaginator.data.map((record: PlantDailyRecord) => {
//             return record.id;
//           }));

//           plantDailyRecordsStore.models.query().whereId(recordsToDelete).delete();

//           recordList.scrollTop = Math.round(recordList.scrollHeight * 0.1);
//           page.value = newPaginator.meta.current_page;
//         });
//     } else if(
//       paginator.value.meta.last_page !== page.value &&
//       scrollable &&
//       recordList.scrollHeight - recordList.scrollTop === recordList.clientHeight
//     ) {
//       fetchRecordsAction.request(props.plant.id, { page: page.value + 1, ...plantsStore.filter.form })
//         .then((newPaginator) => {
//           paginator.value = newPaginator;

//           totalRecords.value += newPaginator.data.length;
//           recordIndex.value = newPaginator.meta.current_page * newPaginator.meta.per_page;

//           const recordsToDelete = recordQueue.enqueueFront(newPaginator.data.map((record: PlantDailyRecord) => {
//             return record.id;
//           }));

//           plantDailyRecordsStore.models.query().whereId(recordsToDelete).delete();

//           recordList.scrollTop = Math.round(recordList.scrollHeight * 0.2);
//           page.value = newPaginator.meta.current_page;
//         });
//     }
//   }
// };

const variationsExpanded = ref(false);

const toggleExpand = () => {
  if(variationsExpanded.value) {
    variationsExpanded.value = false;
  } else {
    variationsExpanded.value = true;
  }
};

const suggestedUsers = ref<{ id: number; name: string }[]>([]);

const getSuggestions = async () => {
  suggestedUsers.value = await projectsStore
    .searchUsers(project.value?.id, { name: nextActionUserName.value });

  return suggestedUsers.value.map((value) => value.name);
};

const selectUser = (index: number) => {
  nextTick(() => {
    if(suggestedUsers.value[index]) {
      searchForm.form.next_action_assignee_id = suggestedUsers.value[index].id;
      searchVariations();
    }
  });
};

const variationsContainer = useTemplateRef('variationsContainer');
const { y } = useScroll(variationsContainer);
const increaseZIndex = ref(true);

watch(y, (newValue) => {
  if(newValue > 0) {
    increaseZIndex.value = false;
  } else {
    increaseZIndex.value = true;
  }
});

const clearUserFilter = () => {
  nextActionUserName.value = '';
  searchForm.form.next_action_assignee_id = undefined;
  searchVariations();
};

const hasActiveFilters = computed(() => {
  return searchForm.isDirty.value || variationsExpanded.value;
});
</script>

<style scoped></style>
